'use client';

import { useState } from 'react';
import { ProviderInfo } from '../../types/ai-settings';
import { motion, AnimatePresence } from 'framer-motion';

interface APIKeyManagerProps {
  providers: ProviderInfo[];
  apiKeys: Record<string, string>;
  onUpdateKey: (provider: string, key: string) => Promise<void>;
  onRemoveKey: (provider: string) => Promise<void>;
}

export function APIKeyManager({
  providers,
  apiKeys,
  onUpdateKey,
  onRemoveKey,
}: APIKeyManagerProps) {
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const handleEditKey = (providerId: string) => {
    setEditingProvider(providerId);
    setKeyInputs({
      ...keyInputs,
      [providerId]: apiKeys[providerId] || '',
    });
  };

  const handleSaveKey = async (providerId: string) => {
    const key = keyInputs[providerId];
    if (!key || key === apiKeys[providerId]) {
      setEditingProvider(null);
      return;
    }

    console.log(`[APIKeyManager] Saving key for provider: ${providerId}`);
    setSavingKey(providerId);
    try {
      await onUpdateKey(providerId, key);
      console.log(`[APIKeyManager] Key saved successfully for: ${providerId}`);
      setEditingProvider(null);
      setKeyInputs({ ...keyInputs, [providerId]: '' });
    } catch (error) {
      console.error('Failed to save API key:', error);
    } finally {
      setSavingKey(null);
    }
  };

  const handleRemoveKey = async (providerId: string) => {
    if (!confirm('Are you sure you want to remove this API key?')) {
      return;
    }

    try {
      await onRemoveKey(providerId);
      setShowKeys({ ...showKeys, [providerId]: false });
    } catch (error) {
      console.error('Failed to remove API key:', error);
    }
  };

  const maskApiKey = (key: string): string => {
    if (!key || key.length < 8) return key;
    const visibleStart = 4;
    const visibleEnd = 4;
    const masked = key.substring(0, visibleStart) + 
                   '•'.repeat(key.length - visibleStart - visibleEnd) + 
                   key.substring(key.length - visibleEnd);
    return masked;
  };

  const toggleShowKey = (providerId: string) => {
    setShowKeys({
      ...showKeys,
      [providerId]: !showKeys[providerId],
    });
  };

  const apiKeyProviders = providers.filter(p => p.requiresApiKey);

  return (
    <div className="bg-white/5 rounded-lg p-4">
      <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
        <span>🔑</span>
        API Keys
      </h3>

      <div className="space-y-3">
        {apiKeyProviders.map((provider) => {
          const hasKey = !!apiKeys[provider.id];
          const isEditing = editingProvider === provider.id;
          const isSaving = savingKey === provider.id;
          const isShowingKey = showKeys[provider.id];

          return (
            <div
              key={provider.id}
              className="p-3 bg-white/5 rounded-lg border border-white/10"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{provider.icon}</span>
                  <span className="text-sm font-medium text-white">
                    {provider.name}
                  </span>
                  {hasKey && (
                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                      Configured
                    </span>
                  )}
                </div>
              </div>

              <AnimatePresence mode="wait">
                {isEditing ? (
                  <motion.div
                    key="editing"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2"
                  >
                    <input
                      type="password"
                      value={keyInputs[provider.id] || ''}
                      onChange={(e) => setKeyInputs({
                        ...keyInputs,
                        [provider.id]: e.target.value,
                      })}
                      placeholder={`Enter ${provider.name} API key`}
                      className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveKey(provider.id)}
                        disabled={isSaving || !keyInputs[provider.id]}
                        className="px-3 py-1 text-xs bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-500/50 text-white rounded transition-colors"
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingProvider(null);
                          setKeyInputs({ ...keyInputs, [provider.id]: '' });
                        }}
                        className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 text-white/80 rounded transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="display"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-2"
                  >
                    {hasKey && (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs text-white/60 font-mono bg-white/5 rounded px-2 py-1">
                          {isShowingKey ? apiKeys[provider.id] : maskApiKey(apiKeys[provider.id])}
                        </code>
                        <button
                          onClick={() => toggleShowKey(provider.id)}
                          className="p-1 text-white/40 hover:text-white/60 transition-colors"
                          title={isShowingKey ? 'Hide key' : 'Show key'}
                        >
                          {isShowingKey ? '🙈' : '👁️'}
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditKey(provider.id)}
                        className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 text-white/80 rounded transition-colors"
                      >
                        {hasKey ? 'Update Key' : 'Add Key'}
                      </button>
                      {hasKey && (
                        <button
                          onClick={() => handleRemoveKey(provider.id)}
                          className="px-3 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
        <p className="text-xs text-yellow-400">
          <span className="font-medium">Security Note:</span> API keys are encrypted and stored locally in your browser. Never share your API keys or commit them to version control.
        </p>
      </div>
    </div>
  );
}